import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import useTranslations from '@/i18n/useTranslations';
import {
  getMuseumLabelThemeVars,
  MUSEUM_LABEL_HEIGHT_CM,
  MUSEUM_LABEL_WIDTH_CM,
} from '@/pages/artwork/limited-edition-room-preview/museumLabel';
import XIcon from '@/shared-components/icons/components/public/XIcon';

import MuseumLabelContent from './MuseumLabelContent';

import './MuseumLabelModal.css';

function getFocusableElements(container) {
  if (!container) {
    return [];
  }

  const selectors = [
    'button',
    'a[href]',
    'area[href]',
    'input:not([type="hidden"])',
    'select',
    'textarea',
    '[tabindex]:not([tabindex="-1"])',
  ];

  return Array.from(container.querySelectorAll(selectors.join(',')))
    .filter((element) => !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden'));
}

export default function MuseumLabelModal({ isOpen, onClose, labelData }) {
  const t = useTranslations('common.shared.artwork_view.room_view');
  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  const safeLabel = useMemo(() => (labelData ? {
    ...labelData,
  } : null), [labelData]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previouslyFocusedElementRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(dialogRef.current);
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first || !dialogRef.current.contains(document.activeElement)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    const body = document.body;
    const originalOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      const focusable = getFocusableElements(dialogRef.current);
      if (focusable.length > 0) {
        focusable[0].focus();
        return;
      }

      if (dialogRef.current && typeof dialogRef.current.focus === 'function') {
        dialogRef.current.focus();
      }
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      body.style.overflow = originalOverflow;

      if (previouslyFocusedElementRef.current && typeof previouslyFocusedElementRef.current.focus === 'function') {
        previouslyFocusedElementRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen || !safeLabel) {
    return null;
  }

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const cardStyle = {
    ...getMuseumLabelThemeVars(safeLabel.theme),
    aspectRatio: `${MUSEUM_LABEL_WIDTH_CM} / ${MUSEUM_LABEL_HEIGHT_CM}`,
  };

  return createPortal(
    (
      <div
        className="museum-label-modal__backdrop"
        role="presentation"
        onMouseDown={handleBackdropMouseDown}
      >
        <div
          className="museum-label-modal__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="museum-label-modal-title"
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="museum-label-modal__content">
            <div className="museum-label-card" style={cardStyle}>
              <MuseumLabelContent
                label={safeLabel}
                titleId="museum-label-modal-title"
                closeButton={(
                  <button
                    type="button"
                    className="museum-label-card__close"
                    onClick={onClose}
                    aria-label={t('close')}
                  >
                    <XIcon />
                  </button>
                )}
              />
            </div>
          </div>
        </div>
      </div>
    ),
    document.body
  );
}
